import { create } from "zustand";
import {
    batchUpdateCategoryPositions,
    batchUpdateEntryPositions,
} from "@/lib/actions/wiki-editor-actions";

// ─── Types ───────────────────────────────────────────────

export interface EntryNode {
    id: string;
    name: string;
    description: string | null;
    parentEntryId: string | null;
    isPublic: boolean;
    pageOrder: number;
    canvasX: number | null;
    canvasY: number | null;
    childCount: number;
    /** Entry's own image — used as the card avatar (and the Overview cover). */
    imageUrl?: string | null;
}

export type WikiView = "canvas" | "grid";

export interface WorldConnection {
    id: string;
    sourceEntryId: string;
    targetEntryId: string;
    relationLabel: string;
    sourceCategoryId: string | null;
    targetCategoryId: string | null;
}

// ─── Helpers ─────────────────────────────────────────────

function buildConnectionIndex(
    connections: WorldConnection[]
): Map<string, Set<string>> {
    const index = new Map<string, Set<string>>();
    for (const c of connections) {
        if (!index.has(c.sourceEntryId)) index.set(c.sourceEntryId, new Set());
        if (!index.has(c.targetEntryId)) index.set(c.targetEntryId, new Set());
        index.get(c.sourceEntryId)!.add(c.targetEntryId);
        index.get(c.targetEntryId)!.add(c.sourceEntryId);
    }
    return index;
}

// ─── Store ───────────────────────────────────────────────

interface WikiState {
    // Core data
    wikiId: string | null;
    /** Monotonic identity for a wiki mount; prevents A → B → A stale responses. */
    wikiGeneration: number;
    categoryEntries: Record<string, EntryNode[]>;
    selectedEntryId: string | null;
    /** Page (child-entry) the drawer should open on; null = the Overview. */
    selectedPageId: string | null;
    /** Distinguishes repeated clicks on the same page chip. */
    selectedPageNonce: number;
    /** Save-aware request for the open drawer to close itself. */
    entryCloseRequestNonce: number;
    worldConnections: WorldConnection[];
    connectionIndex: Map<string, Set<string>>;

    // View + connections toggle + rail search (redesign)
    view: WikiView;
    showConnections: boolean;
    searchQuery: string;
    /** Incrementing request to center the canvas on an entry (rail/grid click). */
    focusRequest: { entryId: string; nonce: number } | null;

    // Dialog state (isolated to prevent canvas re-renders)
    showCreateCategoryDialog: boolean;
    showCreateEntryDialog: boolean;
    createForCategoryId: string | null;
    createForParentEntryId: string | null;

    // Position batching (categories)
    _pendingPositions: Map<string, { x: number; y: number }>;
    _positionTimer: ReturnType<typeof setTimeout> | null;

    // Position batching (entries)
    _pendingEntryPositions: Map<string, { x: number; y: number }>;
    _entryPositionTimer: ReturnType<typeof setTimeout> | null;

    // Actions — synchronous state setters
    setWorldId: (wikiId: string) => void;
    setConnections: (connections: WorldConnection[]) => void;
    setCategoryEntries: (categoryId: string, entries: EntryNode[]) => void;
    updateEntryImage: (categoryId: string, entryId: string, imageUrl: string | null) => void;
    removeCategoryState: (categoryId: string) => void;

    // Actions — entries
    selectEntry: (entryId: string | null, pageId?: string | null) => void;
    requestEntryClose: () => void;

    // Actions — view / toggle / search / focus
    setView: (view: WikiView) => void;
    toggleShowConnections: () => void;
    setSearchQuery: (query: string) => void;
    requestFocusEntry: (entryId: string) => void;

    // Actions — dialogs
    openCreateCategoryDialog: () => void;
    closeCreateCategoryDialog: () => void;
    openCreateEntryDialog: (categoryId: string, parentEntryId?: string | null) => void;
    closeCreateEntryDialog: () => void;

    // Actions — positions (categories)
    queuePositionChange: (id: string, x: number, y: number) => void;
    flushPositions: () => void;

    // Actions — positions (entries)
    queueEntryPositionChange: (id: string, x: number, y: number) => void;
    flushEntryPositions: () => void;
    updateLocalEntryPosition: (entryId: string, x: number, y: number) => void;
}

export const useWikiStore = create<WikiState>((set, get) => ({
    // ── Initial state ──────────────────────────────────
    wikiId: null,
    wikiGeneration: 0,
    categoryEntries: {},
    selectedEntryId: null,
    selectedPageId: null,
    selectedPageNonce: 0,
    entryCloseRequestNonce: 0,
    worldConnections: [],
    connectionIndex: new Map(),

    view: "canvas",
    showConnections: false,
    searchQuery: "",
    focusRequest: null,

    showCreateCategoryDialog: false,
    showCreateEntryDialog: false,
    createForCategoryId: null,
    createForParentEntryId: null,

    _pendingPositions: new Map(),
    _positionTimer: null,

    _pendingEntryPositions: new Map(),
    _entryPositionTimer: null,

    // ── Synchronous state setters ──────────────────────

    setWorldId: (wikiId) => {
        const state = get();
        if (state.wikiId === wikiId) {
            set({ wikiId });
            return;
        }
        if (state._positionTimer) clearTimeout(state._positionTimer);
        if (state._entryPositionTimer) clearTimeout(state._entryPositionTimer);
        set({
                wikiId,
                wikiGeneration: state.wikiGeneration + 1,
                categoryEntries: {},
                selectedEntryId: null,
                selectedPageId: null,
                worldConnections: [],
                connectionIndex: new Map<string, Set<string>>(),
                focusRequest: null,
                showCreateCategoryDialog: false,
                showCreateEntryDialog: false,
                createForCategoryId: null,
                createForParentEntryId: null,
                _pendingPositions: new Map(),
                _positionTimer: null,
                _pendingEntryPositions: new Map(),
                _entryPositionTimer: null,
            });
    },

    setConnections: (connections) => set({
        worldConnections: connections,
        connectionIndex: buildConnectionIndex(connections),
    }),

    setCategoryEntries: (categoryId, entries) =>
        set((state) => ({
            categoryEntries: { ...state.categoryEntries, [categoryId]: entries },
        })),

    updateEntryImage: (categoryId, entryId, imageUrl) => {
        set((state) => {
            const entries = state.categoryEntries[categoryId];
            if (!entries) return state;
            return {
                categoryEntries: {
                    ...state.categoryEntries,
                    [categoryId]: entries.map((entry) =>
                        entry.id === entryId ? { ...entry, imageUrl } : entry
                    ),
                },
            };
        });
    },

    removeCategoryState: (categoryId) => {
        set((state) => {
            const restEntries = { ...state.categoryEntries };
            delete restEntries[categoryId];
            return {
                categoryEntries: restEntries,
                selectedEntryId: null,
            };
        });
    },

    // ── Entry actions ──────────────────────────────────
    // pageId opens the drawer directly on a specific page (e.g. clicking or
    // creating a page chip on the canvas card); omit it for the Overview.
    selectEntry: (entryId, pageId = null) =>
        set((state) => ({
            selectedEntryId: entryId,
            selectedPageId: pageId,
            selectedPageNonce: state.selectedPageNonce + 1,
        })),

    requestEntryClose: () =>
        set((state) => ({ entryCloseRequestNonce: state.entryCloseRequestNonce + 1 })),

    // ── View / toggle / search / focus ─────────────────
    setView: (view) => set({ view }),

    toggleShowConnections: () =>
        set((state) => ({ showConnections: !state.showConnections })),

    setSearchQuery: (query) => set({ searchQuery: query }),

    requestFocusEntry: (entryId) =>
        set((state) => ({
            focusRequest: { entryId, nonce: (state.focusRequest?.nonce ?? 0) + 1 },
        })),

    // ── Dialog actions ─────────────────────────────────
    openCreateCategoryDialog: () => set({ showCreateCategoryDialog: true }),
    closeCreateCategoryDialog: () => set({ showCreateCategoryDialog: false }),

    openCreateEntryDialog: (categoryId, parentEntryId = null) =>
        set({
            showCreateEntryDialog: true,
            createForCategoryId: categoryId,
            createForParentEntryId: parentEntryId,
        }),

    closeCreateEntryDialog: () =>
        set({
            showCreateEntryDialog: false,
            createForCategoryId: null,
            createForParentEntryId: null,
        }),

    // ── Position batching ──────────────────────────────
    queuePositionChange: (id, x, y) => {
        const state = get();
        const nextPending = new Map(state._pendingPositions);
        nextPending.set(id, { x, y });

        if (state._positionTimer) clearTimeout(state._positionTimer);

        const timer = setTimeout(() => get().flushPositions(), 500);
        set({ _pendingPositions: nextPending, _positionTimer: timer });
    },

    flushPositions: () => {
        const { _pendingPositions } = get();
        if (_pendingPositions.size === 0) return;

        const positions = Array.from(_pendingPositions.entries()).map(([id, pos]) => ({
            id,
            x: pos.x,
            y: pos.y,
        }));

        set({ _pendingPositions: new Map(), _positionTimer: null });

        // Fire and forget — no revalidation needed for positions
        batchUpdateCategoryPositions(positions).catch((err) => {
            console.error("Failed to save category positions:", err);
        });
    },

    // ── Entry position batching ────────────────────────
    queueEntryPositionChange: (id, x, y) => {
        const state = get();
        const nextPending = new Map(state._pendingEntryPositions);
        nextPending.set(id, { x, y });
        get().updateLocalEntryPosition(id, x, y);

        if (state._entryPositionTimer) clearTimeout(state._entryPositionTimer);

        const timer = setTimeout(() => get().flushEntryPositions(), 500);
        set({ _pendingEntryPositions: nextPending, _entryPositionTimer: timer });
    },

    flushEntryPositions: () => {
        const { _pendingEntryPositions } = get();
        if (_pendingEntryPositions.size === 0) return;

        const positions = Array.from(_pendingEntryPositions.entries()).map(([id, pos]) => ({
            id,
            x: pos.x,
            y: pos.y,
        }));

        set({ _pendingEntryPositions: new Map(), _entryPositionTimer: null });

        batchUpdateEntryPositions(positions).catch((err) => {
            console.error("Failed to save entry positions:", err);
        });
    },

    updateLocalEntryPosition: (entryId, x, y) => {
        set((state) => {
            const updated: Record<string, EntryNode[]> = {};
            for (const [catId, entries] of Object.entries(state.categoryEntries)) {
                const match = entries.find((e) => e.id === entryId);
                if (match) {
                    updated[catId] = entries.map((e) =>
                        e.id === entryId ? { ...e, canvasX: x, canvasY: y } : e
                    );
                    break;
                }
            }
            if (Object.keys(updated).length === 0) return state;
            return { categoryEntries: { ...state.categoryEntries, ...updated } };
        });
    },
}));
